/**
 * Effets sonores XP — synthétisés via Web Audio (aucun fichier asset).
 * Sobres, déclenchés après un geste utilisateur (clic « Vérifier », fin de quiz…).
 * Coupables via un mute mémorisé (localStorage). Tout est gardé : no-op si Web Audio
 * indisponible ou si muet.
 */

const MUTE_KEY = 'xpSfxMuted';

let audioCtx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) {
    try { audioCtx = new AC(); } catch { return null; }
  }
  // Reprend le contexte si suspendu (politique autoplay).
  if (audioCtx.state === 'suspended') { audioCtx.resume?.(); }
  return audioCtx;
};

export const isSfxMuted = () => {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
};
export const setSfxMuted = (muted) => {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* no-op */ }
};

// Volume maître : adoucit l'ensemble des effets (les gains locaux restent relatifs).
const MASTER = 0.75;

// Joue une note (oscillateur + enveloppe) à `time` (s, relatif au ctx).
const note = (ctx, freq, start, dur, { type = 'sine', gain = 0.10 } = {}) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(gain * MASTER, start + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
};

// Joue une séquence [{f, d, opts}] enchaînée.
const sequence = (steps) => {
  if (isSfxMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  let t = ctx.currentTime + 0.01;
  steps.forEach(({ f, d = 0.12, opts }) => { note(ctx, f, t, d, opts); t += d * 0.85; });
};

export const playCorrect = () => sequence([{ f: 660, d: 0.10 }, { f: 880, d: 0.14 }]);
export const playWrong = () => sequence([{ f: 200, d: 0.18, opts: { type: 'triangle', gain: 0.12 } }]);
export const playAbandon = () => sequence([{ f: 330, d: 0.16 }, { f: 220, d: 0.20 }]);
export const playComplete = () => sequence([{ f: 784, d: 0.12 }, { f: 988, d: 0.16 }]);
export const playLevelUp = () => sequence([
  { f: 523, d: 0.12 }, { f: 659, d: 0.12 }, { f: 784, d: 0.12 }, { f: 1046, d: 0.22 },
]);

// ─── Effets rétention (sélection, jalons, streak, garde-robe, coup de pouce) ───

/** Tick discret à la sélection d'une réponse. */
export const playTick = () => sequence([{ f: 520, d: 0.05, opts: { gain: 0.05 } }]);

/** Jalon de mi-parcours pendant un quiz. */
export const playMilestone = () => sequence([
  { f: 587, d: 0.10, opts: { gain: 0.08 } }, { f: 740, d: 0.14, opts: { gain: 0.08 } },
]);

/** Skin équipé dans la garde-robe. */
export const playEquip = () => sequence([
  { f: 659, d: 0.09, opts: { gain: 0.08 } }, { f: 831, d: 0.09, opts: { gain: 0.08 } }, { f: 988, d: 0.13, opts: { gain: 0.08 } },
]);

/** Palier de série de bonnes réponses — la note finale monte avec le palier. */
export const playStreak = (palier) => {
  const finale = palier >= 10 ? 1318 : palier >= 5 ? 1175 : 1046;
  sequence([{ f: 660, d: 0.09 }, { f: 880, d: 0.09 }, { f: finale, d: 0.16 }]);
};

/** Coup de pouce : deux notes douces descendantes (Tano souffle l'aide). */
export const playHint = () => sequence([
  { f: 740, d: 0.10, opts: { gain: 0.07 } }, { f: 587, d: 0.14, opts: { gain: 0.07 } },
]);
